import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Icon } from "../lib/icons";

export function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/** The one tooltip look — hints (TooltipLayer) and chart / calendar data tips alike. Inverted
 * surface (dark on light theme, light on dark), 12px text. Secondary text inside: text-bg/70. */
export const TIP = "rounded-md bg-text px-2.5 py-1.5 text-xs leading-snug text-bg shadow-pop";

/* ---------- surfaces ---------- */
export function Panel({
  title,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("rounded-xl border border-border bg-surface shadow-card", className)}>
      {(title || actions) && (
        <header className="flex min-h-[52px] flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-border px-4 py-3 sm:px-5">
          <h3 className="text-base font-semibold text-text">{title}</h3>
          {actions}
        </header>
      )}
      <div className="p-4 sm:p-5">{children}</div>
    </section>
  );
}

/** Page title block used by every page: title, one-line subtitle, optional right-side actions. */
export function PageHeader({
  title,
  subtitle,
  actions,
  eyebrow,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  eyebrow?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {eyebrow && <div className="mb-1 text-xs font-medium text-text-2">{eyebrow}</div>}
        <h1 className="truncate text-xl font-semibold leading-tight tracking-tight text-text">{title}</h1>
        {subtitle && <p className="mt-1 text-base text-text-2">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

/* ---------- status badge (icon + text, never colour alone — TZ #31) ---------- */
type Tone = "ok" | "warn" | "crit" | "info" | "neutral";
const TONE: Record<Tone, string> = {
  ok: "bg-ok-weak text-ok",
  warn: "bg-warn-weak text-warn",
  crit: "bg-danger-weak text-danger",
  info: "bg-info-weak text-info",
  neutral: "bg-surface-2 text-text-2",
};
export function Badge({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium", TONE[tone])}>
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
      {children}
    </span>
  );
}

/* ---------- segmented control ---------- */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex min-h-9 gap-0.5 rounded-lg border border-border bg-surface-2 p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded-md px-3 py-1 text-sm font-medium",
            o.value === value
              ? "bg-surface text-accent shadow-[0_1px_2px_rgb(15_23_42/0.08)]"
              : "text-text-2 hover:text-text",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ---------- states ---------- */
// Moving shimmer — a soft light band sweeps across the placeholder, reading as "loading" rather
// than just a dimmed block.
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn("animate-shimmer rounded-md bg-surface-2 bg-[length:200%_100%]", className)}
      style={{
        backgroundImage:
          "linear-gradient(90deg, rgb(var(--surface-2)) 40%, rgb(var(--border)) 50%, rgb(var(--surface-2)) 60%)",
      }}
    />
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="p-10 text-center text-text-2">
      <div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-full bg-surface-2 text-muted">
        <Icon name="search" size={18} />
      </div>
      <div className="font-medium text-text">{title}</div>
      {hint && <div className="mt-1 text-sm">{hint}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 p-10 text-center">
      <div className="grid h-10 w-10 place-items-center rounded-full bg-danger-weak text-danger">
        <Icon name="alert" size={18} />
      </div>
      <div className="text-sm font-semibold text-text">Не удалось загрузить данные</div>
      <div className="max-w-md text-sm text-text-2">{message}</div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium hover:bg-surface-2"
        >
          Повторить
        </button>
      )}
    </div>
  );
}

/* ---------- button ---------- */
export function Button({
  variant = "default",
  size = "md",
  loading,
  className,
  children,
  disabled,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "default" | "primary" | "ghost" | "danger";
  size?: "sm" | "md";
  loading?: boolean;
}) {
  return (
    <button
      {...props}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex items-center rounded-lg font-medium disabled:cursor-not-allowed disabled:opacity-50",
        // the two control heights used everywhere: 36px (md) and 32px (sm)
        size === "md" ? "min-h-9 gap-1.5 px-3.5 py-1.5 text-sm" : "min-h-8 gap-1 px-2.5 py-1 text-xs",
        variant === "primary" &&
          "bg-accent text-white shadow-[0_1px_2px_rgb(37_99_235/0.25)] hover:bg-accent/90 active:bg-accent/80",
        variant === "default" &&
          "border border-border bg-surface text-text shadow-card hover:border-border-strong hover:bg-surface-3 active:bg-surface-2",
        variant === "ghost" && "text-text-2 hover:bg-surface-2 hover:text-text active:bg-border/60",
        variant === "danger" && "bg-danger text-white hover:bg-danger/90 active:bg-danger/80",
        className,
      )}
    >
      {loading && <span className="h-3.5 w-3.5 flex-none animate-spin rounded-full border-2 border-current border-t-transparent" />}
      {children}
    </button>
  );
}
